import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import Icon1 from "@assets/icons/Icon";
import { useRef, useState } from "react";
import { PartnerInfos } from "types";
import { showNotification } from "context/notification.service";
import { useProjectStore } from "@store/projectStore";
import { partnersApi } from "@services/api/partners";

interface Props {
  partnerId: string;
  partnerName: string;
  projectId?: string;
  onClose: () => void;
}

export const AddDecennaleModal = ({ partnerId, partnerName, projectId, onClose }: Props) => {
  const updatePartner = useProjectStore((state) => state.updatePartner);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleSave() {
    if (!startDate || !endDate) {
      showNotification({ type: "error", message: "Veuillez renseigner les dates de début et de fin" });
      return;
    }
    if (startDate >= endDate) {
      showNotification({ type: "error", message: "La date de fin doit être postérieure à la date de début" });
      return;
    }
    if (!selectedFile) {
      showNotification({ type: "error", message: "Veuillez sélectionner un fichier PDF" });
      return;
    }
    setUploading(true);
    try {
      const updated = await partnersApi.uploadDecennale(partnerId, startDate, endDate, selectedFile, projectId);
      updatePartner(updated);
      showNotification({ type: "success", message: "Décennale ajoutée" });
      onClose();
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.message || "";
      showNotification({
        type: "error",
        message: detail ? `Erreur lors de l'upload : ${detail}` : "Erreur lors de l'upload",
      });
      console.error("Decennale upload error:", error?.response?.data || error);
    } finally {
      setUploading(false);
    }
  }

  return (
    <BaseModal
      title={`Ajouter une décennale — ${partnerName}`}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-3 p-4">
        <div className="flex flex-row gap-3">
          <div className="flex flex-col gap-1 flex-1">
            <label className="text-h4 text-neutral-500 font-medium">Date de début</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-neutral-300 rounded-lg px-3 py-2 text-h3"
            />
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <label className="text-h4 text-neutral-500 font-medium">Date de fin</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-neutral-300 rounded-lg px-3 py-2 text-h3"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-h4 text-neutral-500 font-medium">Attestation (PDF)</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) setSelectedFile(file);
            }}
          />
          {selectedFile ? (
            <div
              className="flex items-center gap-2 px-3 py-2 border border-neutral-200 rounded-lg bg-neutral-50 cursor-pointer hover:bg-neutral-100"
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon1 name="pdf" size={16} />
              <span className="text-h3 text-neutral-700 truncate flex-1">{selectedFile.name}</span>
              <button
                className="text-neutral-400 hover:text-red-500"
                onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
              >
                <Icon1 name="effacer2" size={16} />
              </button>
            </div>
          ) : (
            <button
              className="flex items-center gap-2 px-3 py-2 border border-dashed border-neutral-300 rounded-lg text-h3 text-neutral-400 hover:border-neutral-400 hover:text-neutral-600 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon1 name="plus" size={14} />
              Sélectionner un fichier PDF
            </button>
          )}
        </div>
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={uploading ? "Enregistrement..." : "Enregistrer"}
          onClick={handleSave}
          disabled={uploading || !startDate || !endDate || !selectedFile}
        />
      </div>
    </BaseModal>
  );
};
